/**
 * Where Luma can walk (2026-10-05: she walks the tour from feature to feature). A handful of
 * waypoints on open floor and the room's obstacles, all from the room plan; a route is the
 * shortest chain of waypoints whose legs cross no obstacle. Pure, so every leg of the tour is
 * tested.
 *
 * 2026-10-07: the hall is the curved outline from plan.ts (the studio bay included); Luma
 * stays out of the ward's rows and the ward room.
 */
import { WARD } from '../ward/layout.ts';
import {
  ARC,
  ARC_CENTRE,
  PRESENTATION,
  RECEPTION,
  STUDIO,
  STUDIO_FITTINGS,
  TABLE,
  arcPlan,
  distanceToEdges,
  hallOutline,
  insidePolygon,
  receptionPoint,
} from '../world/plan.ts';

export interface Point {
  x: number;
  z: number;
}

/** The table top (keep this far from its centre) and its eight chairs (StudyRoom's seats). */
const TABLE_CLEAR = 1.8;
const CHAIRS = Array.from({ length: 8 }, (_, i) => {
  const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
  return { x: TABLE.x + Math.cos(a) * 2.45, z: TABLE.z + Math.sin(a) * 2.45 };
});
const CHAIR_CLEAR = 0.5;
/** The dashboard arc's band: panels, their floor stages and the wall behind them. */
const ARC_INNER = ARC.radius - 1.6;
/** Keep this far from every wall. */
const WALL_CLEAR = 0.4;
/** The ward: keep out of the rows (just behind the console onward) across the whole fan. */
const WARD_ROWS = { from: WARD.firstRadius - 0.45, halfAngle: WARD.maxHalfAngle + 0.12 };

/** Boxes Luma must not walk through, in room coordinates. */
const BOXES: { minX: number; maxX: number; minZ: number; maxZ: number }[] = [
  // The model shelf and the whiteboard's tray in the studio.
  {
    minX: STUDIO_FITTINGS.shelf.x - 1.0,
    maxX: STUDIO_FITTINGS.shelf.x + 1.0,
    minZ: STUDIO.z0,
    maxZ: STUDIO_FITTINGS.shelf.z + 0.55,
  },
  {
    minX: STUDIO.x0,
    maxX: STUDIO.x0 + 1.05,
    minZ: STUDIO_FITTINGS.trayZ - 0.4,
    maxZ: STUDIO_FITTINGS.trayZ + 0.4,
  },
  // The lectern.
  {
    minX: PRESENTATION.lectern.x - 0.5,
    maxX: PRESENTATION.lectern.x + 0.5,
    minZ: PRESENTATION.lectern.z - 0.5,
    maxZ: PRESENTATION.lectern.z + 0.5,
  },
];

/** The reception counter, as a box in its own frame. */
function hitsCounter(a: Point, b: Point): boolean {
  const toLocal = (p: Point) => {
    const dx = p.x - RECEPTION.x;
    const dz = p.z - RECEPTION.z;
    const c = Math.cos(RECEPTION.yaw);
    const s = Math.sin(RECEPTION.yaw);
    return { x: dx * c - dz * s, z: dx * s + dz * c };
  };
  const la = toLocal(a);
  const lb = toLocal(b);
  const half = RECEPTION.counter.width / 2 + 0.25;
  const depth = RECEPTION.counter.depth / 2 + 0.2;
  return segmentHitsBox(la, lb, { minX: -half, maxX: half, minZ: -depth, maxZ: depth });
}

function segmentHitsBox(
  a: Point,
  b: Point,
  box: { minX: number; maxX: number; minZ: number; maxZ: number },
): boolean {
  // Slab test on the segment's parameter.
  let t0 = 0;
  let t1 = 1;
  for (const [p, d, lo, hi] of [
    [a.x, b.x - a.x, box.minX, box.maxX],
    [a.z, b.z - a.z, box.minZ, box.maxZ],
  ] as const) {
    if (Math.abs(d) < 1e-9) {
      if (p < lo || p > hi) return false;
      continue;
    }
    let ta = (lo - p) / d;
    let tb = (hi - p) / d;
    if (ta > tb) [ta, tb] = [tb, ta];
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return false;
  }
  return true;
}

const HALL_OUTLINE = hallOutline();

/** Whether a point is open floor in the hall, clear of its walls. */
export function onOpenFloor(p: Point): boolean {
  return insidePolygon(p, HALL_OUTLINE) && distanceToEdges(p, HALL_OUTLINE) > WALL_CLEAR;
}

/** Whether a point stands among the ward's rows. */
function inWardRows(p: Point): boolean {
  const dx = p.x - WARD.focal.x;
  const dz = p.z - WARD.focal.z;
  const r = Math.hypot(dx, dz);
  if (r < WARD_ROWS.from) return false;
  const along = dx * WARD.forward.x + dz * WARD.forward.z;
  const across = dx * -WARD.forward.z + dz * WARD.forward.x;
  return Math.abs(Math.atan2(across, along)) < WARD_ROWS.halfAngle;
}

/** Whether a straight walk from a to b stays on open floor. */
export function clear(a: Point, b: Point): boolean {
  const plan = arcPlan();
  const steps = Math.max(2, Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / 0.15));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
    if (!onOpenFloor(p) || inWardRows(p)) return false;
    if (Math.hypot(p.x - TABLE.x, p.z - TABLE.z) < TABLE_CLEAR) return false;
    const r = Math.hypot(p.x - ARC_CENTRE.x, p.z - ARC_CENTRE.z);
    if (CHAIRS.some((c) => Math.hypot(p.x - c.x, p.z - c.z) < CHAIR_CLEAR)) return false;
    // The band ends at the wall; the studio lies beyond it at the same angles.
    if (r > ARC_INNER && r < ARC.radius + 0.6) {
      const angle = Math.atan2(p.z - ARC_CENTRE.z, p.x - ARC_CENTRE.x);
      const a2 = angle < 0 ? angle + Math.PI * 2 : angle;
      // The arc runs from about 166 to 360 degrees, through 270, and into the wall behind.
      if (a2 > plan.start - 0.05 && a2 < plan.end + 0.05) return false;
    }
  }
  if (BOXES.some((box) => segmentHitsBox(a, b, box))) return false;
  return !hitsCounter(a, b);
}

/**
 * Waypoints on open floor: round the table, either side of the reception, the walkway from
 * the door, the west side and into the studio, the presentation display, and the east side
 * by the ward.
 */
export function waypoints(): Point[] {
  const ring = Array.from({ length: 12 }, (_, i) => {
    const a = (i / 12) * Math.PI * 2;
    return { x: TABLE.x + Math.cos(a) * 3.9, z: TABLE.z + Math.sin(a) * 3.9 };
  });
  return [
    ...ring,
    receptionPoint(1.35, -0.55),
    receptionPoint(1.35, 0.75),
    receptionPoint(-1.35, -0.55),
    receptionPoint(-1.35, 0.75),
    { x: 0, z: 10.5 },
    { x: -4.6, z: 10.8 },
    { x: 4.6, z: 10.4 },
    { x: -8.6, z: 6.4 },
    { x: -11.4, z: 8.4 },
    { x: -11.4, z: 12.0 },
    { x: -14.4, z: 8.6 },
    { x: -14.4, z: 12.2 },
    { x: -16.6, z: 10.4 },
    { x: -8.5, z: 12.4 },
    { x: 6.2, z: 6.4 },
    { x: 7.4, z: 9.5 },
    { x: 6.4, z: 12.6 },
  ];
}

/**
 * The route from `from` to `to`: the waypoints to pass and the destination, or a straight
 * line when no route exists (it never should; the tests check every tour leg).
 */
export function walkPath(from: Point, to: Point): Point[] {
  if (clear(from, to)) return [to];
  const nodes = [from, to, ...waypoints()];
  const n = nodes.length;
  const dist = Array<number>(n).fill(Infinity);
  const prev = Array<number>(n).fill(-1);
  const done = Array<boolean>(n).fill(false);
  dist[0] = 0;
  for (;;) {
    let u = -1;
    for (let i = 0; i < n; i++) if (!done[i] && (u < 0 || dist[i]! < dist[u]!)) u = i;
    if (u < 0 || dist[u] === Infinity) break;
    if (u === 1) break;
    done[u] = true;
    for (let v = 0; v < n; v++) {
      if (done[v] || v === u) continue;
      const a = nodes[u]!;
      const b = nodes[v]!;
      const d = dist[u]! + Math.hypot(b.x - a.x, b.z - a.z);
      if (d < dist[v]! && clear(a, b)) {
        dist[v] = d;
        prev[v] = u;
      }
    }
  }
  if (prev[1] === -1) return [to];
  const path: Point[] = [];
  for (let v = 1; v !== 0 && v !== -1; v = prev[v]!) path.unshift(nodes[v]!);
  return path;
}

/** Total length of a walk. */
export function pathLength(from: Point, path: Point[]): number {
  let len = 0;
  let a = from;
  for (const b of path) {
    len += Math.hypot(b.x - a.x, b.z - a.z);
    a = b;
  }
  return len;
}
